// React
import { useCallback } from 'react'

// Components
import { BaseTableHeader, BaseTableFooter, BaseTableAction, BaseTableHeaderRight } from 'components'

// Antd
import { Table, Button } from 'antd'
import { ColumnsType } from 'antd/es/table'

// i18n
import { useTranslation } from 'react-i18next'

// Interfaces
import { ISharedAssessorList } from 'interfaces/shared'
import { ITableEventAttrs } from 'interfaces/table'
import { ISharedAssessorTableProps } from './interfaces'

// Lodash
import cloneDeep from 'lodash/cloneDeep'
import { FormSelectOption } from 'components/form'

// Mobx
import { observer } from 'mobx-react'

// Custom Hook
import { useAuth } from 'hooks'
import { dateUtils_formatDate } from 'utils/common/date.utils'

const KEY = {
  VALIDITY_PERIOD: 'validityPeriod',
  FORMATTED_CREATED_AT: 'formattedCreatedAt',
  FORMATTED_UPDATED_AT: 'formattedUpdatedAt',
}

const SharedAssessorTable = observer(({ data, loading, onAdd, onEdit, onChange, attrs }: ISharedAssessorTableProps) => {
  // Hook
  const { t } = useTranslation()
  const { auth_persisted } = useAuth()

  const tempData = {
    ...data,
    list: data.list.map(list => {
      return {
        ...list,
        formattedCreatedAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
        formattedUpdatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
      }
    }),
  }

  // Common State
  const columns: ColumnsType<ISharedAssessorList> = [
    {
      key: 'number',
      title: t('app.table.number'),
      dataIndex: 'number',
    },
    {
      key: 'userName',
      title: t('contentManagementAssessor.assessor.table.name'),
      dataIndex: 'userName',
      sorter: true,
      sortDirections: ['descend','ascend']
    },
    {
      key: 'bidangSertifikasiName',
      title: t('contentManagementAssessor.assessor.table.field'),
      dataIndex: 'bidangSertifikasiName',
      sorter: true,
      sortDirections: ['descend','ascend']
    },
    {
      key: 'skemaSertifikasiName',
      title: t('contentManagementAssessor.assessor.table.certificationSchema'),
      dataIndex: 'skemaSertifikasiName',
      sorter: true,
      sortDirections: ['descend','ascend']
    },
    {
      key: 'code',
      title: t('app.noReg'),
      dataIndex: 'code',
      sorter: true,
      sortDirections: ['descend','ascend']
    },
    {
      key: KEY.VALIDITY_PERIOD,
      title: t('app.table.validityPeriod'),
      dataIndex: KEY.VALIDITY_PERIOD,
      sorter: (a, b) => new Date(a.startExpiredAt as string).valueOf() - new Date(b.startExpiredAt as string).valueOf(),
    },
    {
      key: KEY.FORMATTED_CREATED_AT,
      title: t('app.table.createdAt'),
      dataIndex: KEY.FORMATTED_CREATED_AT,
      sorter: (a, b) => new Date(a.createdAt as string).valueOf() - new Date(b.createdAt as string).valueOf(),
    },
    {
      key: KEY.FORMATTED_UPDATED_AT,
      title: t('app.table.updatedAt'),
      dataIndex: KEY.FORMATTED_UPDATED_AT,
      sorter: (a, b) => new Date(a.updatedAt as string).valueOf() - new Date(b.updatedAt as string).valueOf(),
    },
    {
      key: 'x',
      title: t('app.table.action'),
      dataIndex: '',
      render: (_, record) => {
        return <BaseTableAction isShow={false} isDelete={false} onClickEdit={() => (onEdit ? onEdit(record) : true)} />
      },
    },
  ]

  /**
   * @description Get filtered columns
   *
   * @return {ColumnsType<ISharedAssessorList>} ColumnsType<ISharedAssessorList>
   */
  const _columns = (): ColumnsType<ISharedAssessorList> => {
    let blacklistedColumns = [] as string[]
    const clonedColumns = cloneDeep(columns)

    if (attrs?.readOnly)
      blacklistedColumns.push(
        ...['id', KEY.FORMATTED_CREATED_AT, 'expiredAt', KEY.FORMATTED_UPDATED_AT, KEY.VALIDITY_PERIOD, 'x'],
      )

    return clonedColumns.filter((column: any) => !blacklistedColumns.includes(column.key))
  }

  /**
   * @description Add new assessor
   *
   * @param {boolean} value
   *
   * @return {void} void
   */
  const onAddAssessor = useCallback((value: boolean): void => {
    if (onAdd) {
      onAdd(value)
    }

    // eslint-disable-next-line
  }, [])

  /**
   * @description Watch any change in table
   *
   * @param {ITableEventAttrs} event
   *
   * @return {void} void
   */
  const onChangeTable = useCallback((event: ITableEventAttrs): void => {
    if (onChange) {
      onChange(event)
    }

    // eslint-disable-next-line
  }, [])

  return (
    <>
      {/* Table Header */}
      <BaseTableHeader
        loading={loading}
        left={
          auth_persisted?.auth_isAuthenticated ? (
            <FormSelectOption
              list={attrs?.fieldDropdown || []}
              loading={attrs?.isFieldLoading}
              disabled={attrs?.isFieldLoading}
              placeholder="contentManagementAssessor.assessor.formPlaceholder.field"
              style={{ width: '100%' }}
              onChange={value => onChangeTable({ name: 'bidangSertifikasiId', value })}
            />
          ) : (
            <></>
          )
        }
        onChange={onChangeTable}
        right={
          attrs?.readOnly ? (
            <></>
          ) : (
            <BaseTableHeaderRight>
              <Button
                type="primary"
                className="custom-button button-table-header"
                loading={loading}
                onClick={() => onAddAssessor(true)}
              >
                {t('app.title.content.create')}
              </Button>
            </BaseTableHeaderRight>
          )
        }
      />

      {/* Table Body */}
      <Table
        onChange={(pagination: any, filters: any, sorter: any) =>
          onChangeTable({ name: 'table', value: { pagination, filters, sorter } })
        }
        columns={_columns()}
        dataSource={[...tempData.list]}
        loading={loading}
        rowKey="id"
        pagination={false}
        className="custom-table"
      />

      {/* Table Footer */}
      <BaseTableFooter onChange={onChangeTable} loading={loading} data={data} />
    </>
  )
})

export { SharedAssessorTable }
